import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import {
  GeneralAdminRoutes,
  SALES_RESERVED_SLUGS,
  SALES_SECTION_ORDER_STEP,
  mainLayoutStyles,
  showNotification,
  slugifySalesPage,
} from "../../../../../constants";
import { useAuth } from "../../../../../hooks";
import {
  createSalesPage,
  getSalesPageById,
  getSalesPages,
  updateSalesPage,
} from "../../hooks/SalesPagesHook";

/**
 * GeneralAdmin > QuickTales.com > Pages > Add / Edit page.
 * Creates or edits a `salesPages` document. See docs/SALES_PAGES_CMS.md
 */
const SalesPageEditContainer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const { currentUser } = useAuth();
  const userId = currentUser?.id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allPages, setAllPages] = useState([]);
  const [previousSlug, setPreviousSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);

  const [form, setForm] = useState({
    title: "",
    slug: "",
    order: "",
    enabled: true,
    showInNav: true,
    isHome: false,
    navColor: "",
  });

  // Multiple pages may share a slug (e.g. several "home" versions), so the slug
  // is always editable and cross-page slug collisions are allowed. At most one
  // page per slug can be enabled at a time (enforced in the hook).
  const pagesSharingSlug = allPages.filter(
    (p) => p.slug === form.slug && p.id !== id
  ).length;

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const init = async () => {
    try {
      setLoading(true);
      const pages = await getSalesPages();
      setAllPages(pages);
      if (isEdit) {
        const data = await getSalesPageById(id);
        if (!data) {
          showNotification("Page not found");
          navigate(GeneralAdminRoutes.salesPagesList);
          return;
        }
        setForm({
          title: data.title || "",
          slug: data.slug || "",
          order: data.order ?? "",
          enabled: data.enabled !== false,
          showInNav: data.showInNav !== false,
          isHome: !!data.isHome,
          navColor: data.navColor || "",
        });
        setPreviousSlug(data.slug || "");
        setSlugTouched(true);
      } else {
        const maxOrder = pages.reduce((m, p) => Math.max(m, p.order || 0), 0);
        setForm((prev) => ({
          ...prev,
          order: maxOrder + SALES_SECTION_ORDER_STEP,
        }));
      }
    } catch (error) {
      console.log("Error initializing page form:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Auto-derive slug from title until the admin edits the slug directly.
  const handleTitleChange = (value) => {
    setForm((prev) => ({
      ...prev,
      title: value,
      slug: slugTouched ? prev.slug : slugifySalesPage(value),
    }));
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    setField("slug", slugifySalesPage(value));
  };

  const validate = () => {
    if (!form.title.trim()) {
      showNotification("Please enter a title");
      return false;
    }
    if (!form.slug.trim()) {
      showNotification("Please enter a slug");
      return false;
    }
    if (form.order === "" || Number.isNaN(Number(form.order))) {
      showNotification("Please enter a valid order number");
      return false;
    }
    if (SALES_RESERVED_SLUGS.includes(form.slug)) {
      showNotification(`"${form.slug}" is a reserved word — choose another slug`);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      const payload = {
        title: form.title.trim(),
        slug: form.slug.trim(),
        order: Number(form.order),
        enabled: form.enabled,
        showInNav: form.showInNav,
        isHome: form.isHome,
        navColor: form.navColor.trim() || null,
      };
      if (isEdit) {
        await updateSalesPage(id, payload, previousSlug, userId);
        showNotification("Page updated");
      } else {
        await createSalesPage(payload, userId);
        showNotification("Page created");
      }
      navigate(GeneralAdminRoutes.salesPagesList);
    } catch (error) {
      console.log("Error saving page:", error.message);
      showNotification("Error saving page");
      setSaving(false);
    }
  };

  const handleCancel = () => navigate(GeneralAdminRoutes.salesPagesList);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={`${isEdit ? "Edit" : "Add"} Page`}
        bgColor="#fff"
        onBackClick={handleCancel}
        editEnabled
        cancelButton
        onCancelClick={handleCancel}
        submitButtonText="Save"
        handleSave={handleSave}
        loading={saving}
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid
          container
          direction="column"
          style={{ padding: 30, marginTop: 40 }}
        >
          <Paper sx={{ p: 3, maxWidth: 640 }}>
            <Typography sx={labelStyles}>Title *</Typography>
            <TextField
              fullWidth
              size="small"
              value={form.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Typography sx={labelStyles}>Slug *</Typography>
            <Typography sx={helperStyles}>
              URL path segment, lowercase. Home is served at /; others at
              /{"{slug}"}. Multiple pages may share a slug — only one enabled at
              a time is served.
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={form.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              sx={{ mb: pagesSharingSlug > 0 ? 1 : 3 }}
            />
            {pagesSharingSlug > 0 ? (
              <Typography sx={{ ...helperStyles, color: "#8a6d3b", mb: 3 }}>
                {pagesSharingSlug} other page
                {pagesSharingSlug > 1 ? "s" : ""} already use this slug.
                Enabling this page will disable the others that share it.
              </Typography>
            ) : null}

            <Typography sx={labelStyles}>Order *</Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={form.order}
              onChange={(e) => setField("order", e.target.value)}
              sx={{ mb: 3, maxWidth: 200 }}
            />

            <Typography sx={labelStyles}>Nav Color (optional)</Typography>
            <Typography sx={helperStyles}>
              CSS color for this nav link (e.g. #7B2FF7). Leave empty for default.
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={form.navColor}
              onChange={(e) => setField("navColor", e.target.value)}
              sx={{ mb: 3, maxWidth: 260 }}
            />

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.enabled}
                    onChange={(e) => setField("enabled", e.target.checked)}
                  />
                }
                label="Enabled"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.showInNav}
                    onChange={(e) => setField("showInNav", e.target.checked)}
                  />
                }
                label="Show in navigation"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isHome}
                    onChange={(e) => setField("isHome", e.target.checked)}
                  />
                }
                label="Home page (served at /)"
              />
            </Box>
            {form.isHome ? (
              <Typography sx={{ ...helperStyles, mt: 1 }}>
                Only one home page can be enabled at a time. Enabling this home
                page disables any other enabled home page.
              </Typography>
            ) : null}
          </Paper>
        </Grid>
      </Grid>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default SalesPageEditContainer;

const labelStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#0A0A0AAB",
  mb: 1,
};

const helperStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 12,
  color: "#808080",
  mb: 1,
};
